import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isTokenExpired, willTokenExpireSoon, getTokenExpirationTime, formatTimeRemaining } from '@/utils/jwtUtils';
import { showErrorToast, showWarningToast, showInfoToast } from '@/lib/toast-utils';
import { clearGroupsCache } from '@/hooks/useGroupChildren';
import { clearUserInfoCache } from '@/hooks/useUserInfo';
import { clearUnitsCache } from '@/lib/unitsCache';
import { clearRolesCache } from '@/lib/rolesCache';
import { clearUnitDetailCache } from '@/lib/unitDetailCache';
import { clearDigitalAssetsCache } from '@/lib/digitalAssetsCache';
import { clearMessageBoardCache } from '@/lib/messageBoardCache';


interface User {
  id: string;
  username: string;
  loginToken: string;
  profileToken: string | null;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ error?: string }>;
  logout: () => void;
  isLoading: boolean;
  recoverPassword: (email: string) => Promise<{ error?: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ error?: string }>;
  refreshToken: () => Promise<{ error?: string }>;
  getValidToken: () => Promise<string | null>;
  getLoginToken: () => Promise<string | null>;
  setProfileToken: (token: string) => void;
  isSessionExpiringSoon: boolean;
  timeUntilExpiration: string | null;
}

type PersistUserUpdate = User | ((currentUser: User | null) => User | null);
type TokenRefreshResult = { token?: string; error?: string };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper to migrate old localStorage format (token → loginToken)
function migrateUserData(data: any): User | null {
  if (!data) return null;
  // Old format had "token", new format has "loginToken"
  if (data.token && !data.loginToken) {
    return {
      id: data.id,
      username: data.username,
      loginToken: data.token,
      profileToken: data.profileToken || null,
    };
  }
  if (data.loginToken) {
    return {
      id: data.id,
      username: data.username,
      loginToken: data.loginToken,
      profileToken: data.profileToken || null,
    };
  }
  return null;
}

function getSelectedProfileId(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedProfile = window.sessionStorage.getItem('selectedProfile');
  if (!storedProfile) {
    return null;
  }

  try {
    const parsedProfile = JSON.parse(storedProfile);
    const profileId = Number(parsedProfile?.id);
    return Number.isFinite(profileId) ? profileId : null;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionExpiringSoon, setIsSessionExpiringSoon] = useState(false);
  const [timeUntilExpiration, setTimeUntilExpiration] = useState<string | null>(null);
  
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningShownRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const loginRefreshPromiseRef = useRef<Promise<TokenRefreshResult> | null>(null);
  const profileRefreshPromiseRef = useRef<Promise<TokenRefreshResult> | null>(null);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    setIsSessionExpiringSoon(false);
    setTimeUntilExpiration(null);
    warningShownRef.current = false;
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    showErrorToast("Your session has expired. Please log in again.", "Session Expired");
  }, []);

  const persistUser = useCallback((updatedUser: PersistUserUpdate) => {
    setUser((currentUser) => {
      const nextUser = typeof updatedUser === 'function'
        ? updatedUser(currentUser)
        : updatedUser;

      if (nextUser) {
        localStorage.setItem('user', JSON.stringify(nextUser));
      } else {
        localStorage.removeItem('user');
      }

      return nextUser;
    });
  }, []);

  // Refresh a specific token
  const refreshSingleToken = useCallback(async (token: string): Promise<TokenRefreshResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('refresh-token', {
        body: { token }
      });

      if (error) {
        console.error('Token refresh error:', error);
        return { error: 'Token refresh failed' };
      }

      if (data?.error) {
        console.error('Token refresh API error:', data.error);
        return { error: data.error };
      }

      if (!data?.token) {
        return { error: 'Invalid refresh response' };
      }

      return { token: data.token };
    } catch (error) {
      console.error('Token refresh failed:', error);
      return { error: 'Token refresh failed' };
    }
  }, []);

  // Refresh loginToken
  const refreshLoginToken = useCallback(async (): Promise<TokenRefreshResult> => {
    if (loginRefreshPromiseRef.current) {
      return loginRefreshPromiseRef.current;
    }

    loginRefreshPromiseRef.current = (async () => {
      const currentLoginToken = user?.loginToken;
      if (!currentLoginToken) return { error: 'No login token to refresh' };

      const result = await refreshSingleToken(currentLoginToken);
      if (result.error) return { error: result.error };

      persistUser((currentUser) => {
        if (!currentUser) return null;
        return { ...currentUser, loginToken: result.token! };
      });

      console.log('Login token refreshed successfully');
      return { token: result.token };
    })();

    try {
      return await loginRefreshPromiseRef.current;
    } finally {
      loginRefreshPromiseRef.current = null;
    }
  }, [user?.loginToken, refreshSingleToken, persistUser]);

  // Profile tokens are short-lived and must be re-issued from the current login token.
  const refreshProfileToken = useCallback(async (): Promise<TokenRefreshResult> => {
    if (profileRefreshPromiseRef.current) {
      return profileRefreshPromiseRef.current;
    }

    profileRefreshPromiseRef.current = (async () => {
      if (!user?.profileToken) {
        return { error: 'No profile token to refresh' };
      }

      const profileUserId = getSelectedProfileId();
      if (!profileUserId) {
        return { error: 'No selected profile for token refresh' };
      }

      let loginToken = user.loginToken;
      if (!loginToken) {
        return { error: 'No login token available' };
      }

      if (willTokenExpireSoon(loginToken, 300)) {
        const loginRefreshResult = await refreshLoginToken();
        if (loginRefreshResult.error) {
          return { error: loginRefreshResult.error };
        }

        if (!loginRefreshResult.token) {
          return { error: 'Invalid login token refresh response' };
        }

        loginToken = loginRefreshResult.token;
      }

      try {
        const { data, error } = await supabase.functions.invoke('switch-profile', {
          body: { token: loginToken, profileUserId }
        });

        if (error) {
          console.error('Profile token refresh error:', error);
          return { error: 'Profile token refresh failed' };
        }

        if (data?.error) {
          console.error('Profile token refresh API error:', data.error);
          return { error: data.error };
        }

        if (!data?.token) {
          return { error: 'Invalid profile token refresh response' };
        }

        persistUser((currentUser) => {
          if (!currentUser) return null;
          return { ...currentUser, profileToken: data.token };
        });

        console.log('Profile token refreshed successfully');
        return { token: data.token };
      } catch (error) {
        console.error('Profile token refresh failed:', error);
        return { error: 'Profile token refresh failed' };
      }
    })();

    try {
      return await profileRefreshPromiseRef.current;
    } finally {
      profileRefreshPromiseRef.current = null;
    }
  }, [user?.loginToken, user?.profileToken, refreshLoginToken, persistUser]);

  // Legacy refreshToken — refreshes whichever is the "active" token
  const refreshToken = useCallback(async (): Promise<{ error?: string }> => {
    if (user?.profileToken) {
      return refreshProfileToken();
    }
    return refreshLoginToken();
  }, [user, refreshLoginToken, refreshProfileToken]);

  // Returns the profileToken if available, otherwise loginToken (with refresh if expired)
  const getValidToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;

    const activeToken = user.profileToken || user.loginToken;
    if (!activeToken) return null;

    if (willTokenExpireSoon(activeToken, 300)) {
      console.log(isTokenExpired(activeToken)
        ? 'Active token expired, attempting refresh...'
        : 'Active token expiring soon, attempting refresh...');
      const refreshResult = user.profileToken ? await refreshProfileToken() : await refreshLoginToken();
      if (refreshResult.error) {
        console.error('Failed to refresh expired token:', refreshResult.error);
        handleSessionExpired();
        return null;
      }

      return refreshResult.token || null;
    }

    return activeToken;
  }, [user, refreshLoginToken, refreshProfileToken, handleSessionExpired]);

  // Returns the loginToken specifically (for password change)
  const getLoginToken = useCallback(async (): Promise<string | null> => {
    if (!user?.loginToken) return null;

    if (willTokenExpireSoon(user.loginToken, 300)) {
      console.log(isTokenExpired(user.loginToken)
        ? 'Login token expired, attempting refresh...'
        : 'Login token expiring soon, attempting refresh...');
      const refreshResult = await refreshLoginToken();
      if (refreshResult.error) {
        console.error('Failed to refresh login token:', refreshResult.error);
        handleSessionExpired();
        return null;
      }

      return refreshResult.token || null;
    }

    return user.loginToken;
  }, [user, refreshLoginToken, handleSessionExpired]);

  // Set profile token after profile switch
  const setProfileTokenFn = useCallback((token: string) => {
    if (!user) return;
    persistUser((currentUser) => {
      if (!currentUser) return null;
      return { ...currentUser, profileToken: token };
    });
    warningShownRef.current = false;
  }, [user, persistUser]);

  const validateToken = useCallback(() => {
    if (!user) return;

    const activeToken = user.profileToken || user.loginToken;
    if (!activeToken) return;

    // Validate loginToken
    if (user.loginToken && isTokenExpired(user.loginToken)) {
      console.log('Login token has expired during validation, attempting refresh...');
      void (async () => {
        const result = await refreshLoginToken();
        if (result.error) {
          console.error('Login token refresh failed:', result.error);
          handleSessionExpired();
        }
      })();
      return;
    }

    // Validate profileToken if present
    if (user.profileToken && isTokenExpired(user.profileToken)) {
      console.log('Profile token expired, attempting refresh...');
      void (async () => {
        const result = await refreshProfileToken();
        if (result.error) {
          console.error('Profile token refresh failed:', result.error);
          handleSessionExpired();
        }
      })();
      return;
    }

    const timeLeft = getTokenExpirationTime(activeToken);
    if (timeLeft !== null) {
      setTimeUntilExpiration(formatTimeRemaining(timeLeft));
      
      const expiringSoon = willTokenExpireSoon(activeToken, 600);
      setIsSessionExpiringSoon(expiringSoon);
      if (!expiringSoon) {
        warningShownRef.current = false;
      }
      
      if (expiringSoon && !warningShownRef.current) {
        warningShownRef.current = true;
        showWarningToast(`Your session will expire in ${formatTimeRemaining(timeLeft)}. It will be automatically refreshed.`, "Session Expiring Soon");
      }
      
      const now = Date.now();
      const recentActivity = now - lastActivityRef.current < 30 * 60 * 1000;
      const shouldRefreshLoginToken = willTokenExpireSoon(user.loginToken, 300);
      const shouldRefreshProfileToken = user.profileToken ? willTokenExpireSoon(user.profileToken, 300) : false;

      if (recentActivity && (shouldRefreshLoginToken || shouldRefreshProfileToken)) {
        console.log('Auto-refreshing tokens due to upcoming expiration');

        void (async () => {
          if (shouldRefreshLoginToken) {
            const loginRefreshResult = await refreshLoginToken();
            if (loginRefreshResult.error) {
              console.error('Auto-refresh login token failed:', loginRefreshResult.error);
              handleSessionExpired();
              return;
            }
          }

          if (shouldRefreshProfileToken) {
            const profileRefreshResult = await refreshProfileToken();
            if (profileRefreshResult.error) {
              console.error('Auto-refresh profile token failed:', profileRefreshResult.error);
              handleSessionExpired();
            }
          }
        })();
      }
    } else {
      setTimeUntilExpiration(null);
      setIsSessionExpiringSoon(false);
      warningShownRef.current = false;
    }
  }, [user, handleSessionExpired, refreshLoginToken, refreshProfileToken]);

  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  useEffect(() => {
    if (user?.loginToken) {
      validateToken();
      
      intervalRef.current = setInterval(validateToken, 60000);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [user?.loginToken, user?.profileToken, validateToken]);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = migrateUserData(JSON.parse(savedUser));
        
        if (!parsedUser || isTokenExpired(parsedUser.loginToken)) {
          console.log('Saved login token is expired, clearing session');
          localStorage.removeItem('user');
        } else {
          setUser(parsedUser);
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      if ((response.status === 401 || response.status === 403) && user) {
        // Don't intercept refresh-token calls — those are handled by the refresh logic itself
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
        if (url.includes('refresh-token')) {
          return response;
        }
        const responseText = await response.clone().text();
        if (responseText.includes('authentication') || responseText.includes('token') || responseText.includes('unauthorized')) {
          handleSessionExpired();
        }
      }
      
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [user, handleSessionExpired]);

  const login = async (username: string, password: string): Promise<{ error?: string }> => {
    try {
      // Local development test login bypass.
      // Skips the external staging API entirely — only available in dev builds.
      if (
        import.meta.env.DEV &&
        username === 'devadmin' &&
        password === 'devadmin'
      ) {
        // Synthesize a JWT-shaped token (header.payload.signature, base64url) so
        // jwtUtils + validateToken treat it as a valid, non-expired token.
        const b64url = (obj: unknown) =>
          btoa(JSON.stringify(obj))
            .replace(/=+$/, '')
            .replace(/\+/g, '-')
            .replace(/\//g, '_');
        const header = b64url({ alg: 'none', typ: 'JWT' });
        const payload = b64url({
          sub: 'devadmin',
          name: 'Dev Admin',
          // Expires in ~10 years
          exp: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 365 * 10,
          iat: Math.floor(Date.now() / 1000),
          dev: true,
        });
        const fakeToken = `${header}.${payload}.devsig`;

        const userData: User = {
          id: 'devadmin',
          username: 'devadmin',
          loginToken: fakeToken,
          profileToken: null,
        };
        persistUser(userData);
        sessionStorage.removeItem('hidePrices');
        console.info('[AuthContext] Dev test login active — external API bypassed.');
        return {};
      }

      const { data: authData, error: authError } = await supabase.functions.invoke('authenticate', {
        body: { 
          username,
          password,
          shopId: "",
          expirationInSeconds: "86400"
        }
      });

      if (authError) {
        return { error: 'Your username or password is invalid. Please try again' };
      }

      if (authData && authData.error) {
        return { error: 'Your username or password is invalid. Please try again' };
      }

      if (!authData?.token) {
        return { error: 'Invalid credentials' };
      }

      const userData: User = {
        id: username,
        username,
        loginToken: authData.token,
        profileToken: null,
      };

      persistUser(userData);
      sessionStorage.removeItem('hidePrices');
      
      return {};
    } catch (error) {
      return { error: 'Login failed. Please try again.' };
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    sessionStorage.removeItem('hidePrices');
    sessionStorage.removeItem('selectedProfile');
    sessionStorage.removeItem('userProfiles');
    setIsSessionExpiringSoon(false);
    setTimeUntilExpiration(null);
    warningShownRef.current = false;
    clearGroupsCache();
    clearUserInfoCache();
    clearUnitsCache();
    clearRolesCache();
    clearUnitDetailCache();
    clearDigitalAssetsCache();
    clearMessageBoardCache();
    

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const recoverPassword = async (email: string): Promise<{ error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('recover-password', {
        body: { email }
      });

      if (error) {
        return { error: `Password recovery failed: ${error.message}` };
      }

      if (data && data.error) {
        return { error: `Password recovery failed: ${data.error}` };
      }

      return {};
    } catch (error) {
      return { error: 'Password recovery failed. Please try again.' };
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<{ error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('reset-password', {
        body: { token, newPassword }
      });

      if (error) {
        return { error: `Password reset failed: ${error.message}` };
      }

      if (data && data.error) {
        return { error: `Password reset failed: ${data.error}` };
      }

      return {};
    } catch (error) {
      return { error: 'Password reset failed. Please try again.' };
    }
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    recoverPassword,
    resetPassword,
    refreshToken,
    getValidToken,
    getLoginToken,
    setProfileToken: setProfileTokenFn,
    isSessionExpiringSoon,
    timeUntilExpiration,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
